import { CSSProperties, MutableRefObject, useCallback, useEffect, useRef, useState } from "react";

/**
 * The subset of Recharts' chart mouse-event state that pinning reads. Recharts
 * 1.x types its chart mouse handlers loosely, so this documents the two fields
 * we actually depend on.
 */
export interface IChartMouseState {
  /** x-axis value under the pointer (the chart's `dataKey`, a timestamp here). */
  activeLabel?: number | string;
  /** Series values at that x position. */
  activePayload?: any[];
}

/**
 * A single point on a Recharts graph that has been "pinned" — i.e. the
 * parameter tooltip should stay open at this point regardless of further
 * mouse movement, instead of following the hovered point like the default
 * Recharts `Tooltip` does.
 */
export interface IPinnedChartPoint {
  /** The x-axis value of the pinned point (Recharts `activeLabel` — typically a timestamp). */
  label: any;
  /** The series data for the pinned point (Recharts `activePayload`), passed straight into the tooltip's own content renderer. */
  payload: any;
  /**
   * Where to draw the pinned window, in px relative to the chart container.
   * Filled in by `pin()` itself by measuring where Recharts had just drawn its
   * own hover tooltip — callers don't pass this.
   */
  position?: { left: number; top: number };
}

/**
 * Return value of {@link usePinnableChartTooltip}. Declared explicitly (rather
 * than left inferred) so these doc comments actually show up for consumers
 * on hover/autocomplete — TS does not reliably surface JSDoc from a hook's
 * internal locals through an inferred return-object type.
 */
export interface IUsePinnableChartTooltipResult {
  /** The currently pinned point, or `null` when nothing is pinned (default hover behavior applies). */
  pinnedPoint: IPinnedChartPoint | null;
  /** Pins the tooltip at the given point, at the position Recharts' hover tooltip currently occupies. */
  pin: (point: IPinnedChartPoint) => void;
  /** Clears the pin, reverting to normal hover behavior. */
  unpin: () => void;
  /** Attach to the element that wraps the whole chart. Must be a positioned element (`position: relative`) — the pinned window is placed relative to it. */
  containerRef: MutableRefObject<HTMLDivElement | null>;
  /** Attach to the pinned window's own DOM node — a click inside it (e.g. on a button) never unpins. */
  tooltipRef: MutableRefObject<HTMLDivElement | null>;
  /** `left`/`top` (px) to spread onto the pinned window's inline `style`, or `undefined` when nothing is pinned. */
  pinnedTooltipStyle: CSSProperties | undefined;
}

/**
 * Shared pin/unpin state machine for a graph's parameter tooltip.
 *
 * A plain Recharts `Tooltip` only shows while the mouse hovers the chart —
 * it closes on mouse-leave and can't be clicked into (default
 * `pointer-events: none`). This hook lets a graph component "pin" the
 * tooltip at a clicked point so it stays open and becomes interactive
 * (e.g. so a button rendered inside it, like "Open System Diagnostic",
 * can actually be clicked).
 *
 * Positioning is measured, not recalculated: `pin()` reads the live
 * `.recharts-tooltip-wrapper` rect at click time, so the pinned window
 * appears exactly where the hover tooltip already was. Recharts applies its
 * own offset and edge-flipping to that wrapper, so measuring it is the only
 * way to avoid the pinned window landing somewhere else than the hover one.
 *
 * Two rules the consuming component must follow, or pin/unpin breaks:
 *
 * 1. **Keep `<Tooltip>` mounted even while pinned.** Recharts only computes
 *    `activeLabel` / `activePayload` / `activeCoordinate` on its mouse events
 *    when a `Tooltip` child exists. Unmount it and the next click reports no
 *    active point, so there is nothing to toggle off. Hide the *content*
 *    instead (return `null` from the content renderer while pinned).
 * 2. **Toggle inside the chart's own mouse-up**, not via this hook's
 *    outside-click. Clicks on the chart are deliberately ignored here.
 *
 * @example
 * const { pinnedPoint, pin, unpin, containerRef, tooltipRef, pinnedTooltipStyle } =
 *   usePinnableChartTooltip();
 *
 * // drop the pin whenever the zoom/date domain changes under it
 * useEffect(() => { unpin(); }, [XDomain, unpin]);
 *
 * <div ref={containerRef} className={classes.chartContainer}>
 *   <ComposedChart onMouseUp={(e) => {
 *     const isClickNotDrag = refAreaLeft === refAreaRight || refAreaRight === "";
 *     if (e && (e.activeLabel || e.activeLabel === 0) && isClickNotDrag) {
 *       if (pinnedPoint) {
 *         unpin();
 *       } else {
 *         pin({ label: e.activeLabel, payload: e.activePayload });
 *       }
 *     }
 *     zoom(e); // existing zoom logic, untouched
 *   }}>
 *     // stays mounted while pinned — see rule 1 above
 *     <Tooltip content={<CustomTooltip />} />
 *   </ComposedChart>
 *   {pinnedPoint && (
 *     <div
 *       ref={tooltipRef}
 *       className={classes.pinnedTooltip}
 *       style={pinnedTooltipStyle}
 *     >
 *       <CustomTooltip isPinnedRender payload={pinnedPoint.payload} label={pinnedPoint.label} />
 *     </div>
 *   )}
 * </div>
 */
export const usePinnableChartTooltip = (): IUsePinnableChartTooltipResult => {
  const [pinnedPoint, setPinnedPoint] = useState<IPinnedChartPoint | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  const pin = useCallback((point: IPinnedChartPoint) => {
    const container = containerRef.current;
    const hoverWrapper = container?.querySelector(".recharts-tooltip-wrapper");
    let position: IPinnedChartPoint["position"];

    if (container && hoverWrapper) {
      const hoverRect = hoverWrapper.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      if (hoverRect.width && hoverRect.height) {
        position = {
          left: hoverRect.left - containerRect.left,
          top: hoverRect.top - containerRect.top
        };
      }
    }

    setPinnedPoint({ ...point, position });
  }, []);

  const unpin = useCallback(() => {
    setPinnedPoint(null);
  }, []);

  // While pinned, a mousedown anywhere outside the chart and outside the
  // pinned window itself unpins. Clicks on the chart are skipped on purpose:
  // the chart's own mouse-up owns the toggle, and unpinning here first would
  // let that mouse-up immediately re-pin the same point.
  //
  // Listens on the capture phase (like MUI's own ClickAwayListener) so a
  // descendant's `stopPropagation()` — e.g. a sidebar nav item's own click
  // handling — can't stop this from firing before the event reaches it.
  useEffect(() => {
    if (!pinnedPoint) {
      return;
    }

    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (tooltipRef.current?.contains(target)) {
        return;
      }
      // `.recharts-wrapper` is the element Recharts binds its own mouse
      // handlers to — matching it exactly means the surrounding container
      // chrome (legend, zoom hint, Reset Zoom, padding) still counts as
      // "outside" and unpins, which is what the design asks for.
      const chartWrapper = containerRef.current?.querySelector(".recharts-wrapper");
      if (chartWrapper?.contains(target)) {
        return;
      }
      unpin();
    };

    document.addEventListener("mousedown", handleOutsideClick, true);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick, true);
    };
  }, [pinnedPoint, unpin]);

  const pinnedTooltipStyle = pinnedPoint?.position
    ? { left: pinnedPoint.position.left, top: pinnedPoint.position.top }
    : undefined;

  return { pinnedPoint, pin, unpin, containerRef, tooltipRef, pinnedTooltipStyle };
};

export default usePinnableChartTooltip;
